import {FC, useMemo, useState} from 'react';
import {
  EditUserBtn,
  SystemDetailsUsersBlock,
  SystemDetailSystemInfoWrapper,
} from '@pages/SystemDetailsPage.styles';
import {Edit} from '@carbon/icons-react';
import UsersBlock from '@molecules/UsersBlock';
import {theme} from '@styles/theme';
import CoverageBlock from '@molecules/CoverageBlock';
import {
  SubsystemCoverageWrapper,
  SubsystemTitleWrapper,
} from '@organisms/SubsystemDetailsBlock.styles';
import Text from '@atoms/Text';
import CircleButton from '@atoms/CircleButton';
import Accordion from '@molecules/Accordion';
import ModulesTable from '@organisms/ModulesTable';
import EditTestCoverageModal from '@organisms/modals/EditTestCoverageModal';
import {IModule, ISubsystem, ICell} from '@types';
import AddEditModuleModal from '@organisms/modals/AddEditModuleModal';
import DeleteSubsystemModal from '@organisms/modals/DeleteSubsystemModal';
import DeleteModuleModal from '@organisms/modals/DeleteModuleModal';
import AddEditSubsystemModal from '@organisms/modals/AddEditSubsystemModal';
import {useSelector} from 'react-redux';
import {RootState} from '@store';
import {EUserRoles, EUserScopedRoles} from '@constants/usersRoles';

export interface SubsystemDetailsBlockProps {
  subsystem: ISubsystem;
}

export const SubsystemDetailsBlock: FC<SubsystemDetailsBlockProps> = ({subsystem}) => {
  const [showEditSubsystem, setShowEditSubsystem] = useState(false);
  const [showAddModule, setShowAddModule] = useState(false);
  const [showDeleteModule, setShowDeleteModule] = useState(false);
  const [showEditTestCoverage, setShowEditTestCoverage] = useState(false);
  const [showDeleteSubsystem, setShowDeleteSubsystem] = useState(false);
  const [selectedModule, setSelectedModule] = useState<IModule | undefined>();
  const [selectedTest, setSelectedTest] = useState<ICell | undefined>();
  const systemOwner = subsystem?.users.filter(user => user.scoped_role_id === 4) || [];
  const administrators = subsystem?.users.filter(user => user.scoped_role_id === 3) || [];
  const editors = subsystem?.users.filter(user => user.scoped_role_id === 1) || [];
  const viewers = subsystem?.users.filter(user => user.scoped_role_id === 2) || [];
  const currentUserID = useSelector((state: RootState) => state.profile.profile?.id);
  const systemRole = useSelector((state: RootState) => state.profile.profile?.role_id);
  const scopedRole = useMemo(() => {
    return subsystem?.users.find(user => user.id === currentUserID)?.scoped_role_id;
  }, [subsystem?.users]);

  const onEditTest = (module: IModule, cell: ICell) => {
    setSelectedModule(module);
    setSelectedTest(cell);
    setShowEditTestCoverage(true);
  };

  return (
    <SystemDetailSystemInfoWrapper>
      <SubsystemTitleWrapper>
        <Text text={`${subsystem.id}. ${subsystem.name}`} type="titleR" />
        <CircleButton
          key={'system-menu'}
          menu={
            systemRole === EUserRoles.BASE_USER && scopedRole === EUserScopedRoles.VIEWER
              ? []
              : [
                  {title: 'Редактировать', onClick: () => setShowEditSubsystem(true)},
                  {
                    title: 'Добавить модуль',
                    onClick: () => {
                      setShowAddModule(true);
                    },
                  },
                  {
                    title: 'Удалить',
                    onClick: () => {
                      setShowDeleteSubsystem(true);
                    },
                    color: theme.colors.signalRed00,
                  },
                ]
          }
        />
      </SubsystemTitleWrapper>
      <SubsystemCoverageWrapper>
        <CoverageBlock
          autoCoverage={subsystem?.auto_coverage}
          manualCoverage={subsystem?.coverage}
          tests={subsystem?.test_types}
          chartWidth="50%"
        />
      </SubsystemCoverageWrapper>
      <SystemDetailsUsersBlock>
        <UsersBlock
          users={administrators}
          systemOwner={systemOwner[0] || undefined}
          title="Администраторы"
        />
        {!!editors.length && <UsersBlock users={editors} title="Редакторы" />}
        {!!viewers.length && <UsersBlock users={viewers} title="Наблюдатели" />}
        {(systemRole !== EUserRoles.BASE_USER ||
          scopedRole !== EUserScopedRoles.VIEWER) && (
          <EditUserBtn onClick={() => setShowEditSubsystem(true)}>
            <Edit width={14} height={13} color={theme.colors.basic00} />
          </EditUserBtn>
        )}
      </SystemDetailsUsersBlock>
      <Accordion
        content={
          <ModulesTable
            modules={subsystem.modules}
            onAddModule={() => setShowAddModule(true)}
            onEditModule={module => {
              setSelectedModule(module);
              setShowAddModule(true);
            }}
            onSortTests={() => {}}
            onEditTest={onEditTest}
            systemRole={systemRole}
            scopedRole={scopedRole}
          />
        }
      />
      <AddEditSubsystemModal
        isOpened={showEditSubsystem}
        onCloseRequest={() => setShowEditSubsystem(false)}
        subsystem={subsystem}
      />
      <EditTestCoverageModal
        isOpened={showEditTestCoverage}
        onCloseRequest={() => {
          setShowEditTestCoverage(false);
          setSelectedTest(undefined);
          setSelectedModule(undefined);
        }}
        module={selectedModule}
        test={selectedTest}
      />
      <AddEditModuleModal
        isOpened={showAddModule}
        onCloseRequest={() => {
          setShowAddModule(false);
          setSelectedModule(undefined);
        }}
        onDelete={() => {
          setShowAddModule(false);
          setShowDeleteModule(true);
        }}
        module={selectedModule}
        subsystem_id={subsystem.id}
      />
      <DeleteModuleModal
        isOpened={showDeleteModule}
        onCloseRequest={() => {
          setShowDeleteModule(false);
          setSelectedModule(undefined);
        }}
        module={selectedModule}
      />
      <DeleteSubsystemModal
        isOpened={showDeleteSubsystem}
        subsystem={subsystem}
        onCloseRequest={() => setShowDeleteSubsystem(false)}
      />
    </SystemDetailSystemInfoWrapper>
  );
};

export default SubsystemDetailsBlock;
